import React, { useState, useEffect, useRef } from "react";

// -------------------- UTILITY FUNCTIONS --------------------

// Parse timestamp to { year, month, day } without using new Date
export const parseTimestamp = (timestamp) => {
  const msPerDay = 24 * 60 * 60 * 1000;
  const daysSinceEpoch = Math.floor(timestamp / msPerDay);

  let year = 1970;
  let dayCount = daysSinceEpoch;
  const isLeap = (y) => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;

  while (true) {
    const daysInYear = isLeap(year) ? 366 : 365;
    if (dayCount >= daysInYear) {
      dayCount -= daysInYear;
      year++;
    } else break;
  }

  const monthDays = [
    31,
    isLeap(year) ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ];
  let month = 0;
  while (dayCount >= monthDays[month]) {
    dayCount -= monthDays[month];
    month++;
  }

  const day = dayCount + 1;
  return { year, month: month + 1, day };
};

// Format timestamp to yyyy-mm-dd
export const formatDisplayDate = (timestamp) => {
  if (!timestamp) return "";
  const d = parseTimestamp(timestamp);
  return `${d.year}-${String(d.month).padStart(2, "0")}-${String(
    d.day
  ).padStart(2, "0")}`;
};

// Add/subtract days in timestamp
export const addDays = (timestamp, days) =>
  timestamp + days * 24 * 60 * 60 * 1000;

// Days in month
export const daysInMonth = (year, month) => {
  const md = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (month === 2 && ((year % 4 === 0 && year % 100 !== 0) || year % 400 === 0))
    return 29;
  return md[month - 1];
};

// -------------------- MAIN COMPONENT --------------------
const ServerDatePicker = ({ fetchServerTime, onChange, type }) => {
  const dropdownRef = useRef(null);
  const [hoverDate, setHoverDate] = useState(null);
  const [serverTime, setServerTime] = useState(null);
  const [selectedOption, setSelectedOption] = useState("realTradingDefault1");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [startMonthOffset, setStartMonthOffset] = useState(1);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // ---------------- FETCH SERVER TIME ON MOUNT ----------------
  useEffect(() => {
    const initServerTime = async () => {
      const data = await fetchServerTime();
      if (data?.success) {
        const ts = data.adjustedTimestamp;
        let oneDay = 24 * 60 * 60 * 1000;
        if (type === "supportdeshboard") {
          const startOfDay = (ts) => ts - (ts % oneDay);
          const endOfDay = (ts) => startOfDay(ts) + oneDay - 1000;
          setServerTime(ts);
          let end = endOfDay(ts);
          let start = startOfDay(ts - 29 * oneDay);
          setStartDate(start);
          setEndDate(end);
          if (onChange) onChange(start, end);
          setSelectedOption("realTradingDefault4");
        } else {
          const startOfDay = (ts) => ts - (ts % oneDay);
          const endOfDay = (ts) => startOfDay(ts) + oneDay - 1000;
          setServerTime(ts);
          let start = startOfDay(ts);
          let end = endOfDay(ts);
          setStartDate(start);
          setEndDate(end);
          if (onChange) onChange(start, end);
        }
      }
    };
    initServerTime();
  }, []);

  // ---------------- HANDLE RADIO CHANGE ----------------
  const handleRadioChange = (id) => {
    if (!serverTime) return;

    setSelectedOption(id);
    if (id !== "realTradingDefault8") setIsDropdownOpen(false);

    // Constants
    const oneDay = 24 * 60 * 60 * 1000;

    // Helpers
    const startOfDay = (ts) => ts - (ts % oneDay);
    const endOfDay = (ts) => startOfDay(ts) + oneDay - 1000; // 23:59:59
    const addDays = (ts, days) => ts + days * oneDay;

    let start, end;

    switch (id) {
      // ---------------- Today ----------------
      case "realTradingDefault1":
        start = startOfDay(serverTime);
        end = endOfDay(serverTime);
        break;

      // ---------------- Yesterday ----------------
      case "realTradingDefault2":
        const yesterday = addDays(serverTime, -1);
        start = startOfDay(yesterday);
        end = endOfDay(yesterday);
        break;

      // ---------------- Last 7 Days ----------------
      case "realTradingDefault3":
        start = startOfDay(addDays(serverTime, -6)); // 7 days including today
        end = endOfDay(serverTime);
        break;

      // ---------------- Last 30 Days ----------------
      case "realTradingDefault4":
        start = startOfDay(addDays(serverTime, -29)); // 30 days including today
        end = endOfDay(serverTime);
        break;

      // ---------------- Current Month ----------------
      case "realTradingDefault5": {
        const d = parseTimestamp(serverTime);
        const firstDayOffset = (d.day - 1) * oneDay;
        start = startOfDay(serverTime - firstDayOffset);
        end = endOfDay(serverTime);
        break;
      }

      // ---------------- Previous Month ----------------
      case "realTradingDefault6": {
        const d = parseTimestamp(serverTime);
        const prevMonth = d.month === 1 ? 12 : d.month - 1;
        const year = d.month === 1 ? d.year - 1 : d.year;
        const prevMonthDays = daysInMonth(year, prevMonth);

        // Start → 1st day of previous month 00:00:00
        start = startOfDay(
          serverTime - d.day * oneDay - (prevMonthDays - 1) * oneDay
        );

        // End → last day of previous month 23:59:59
        end = endOfDay(serverTime - d.day * oneDay);
        break;
      }

      // ---------------- All Time ----------------
      case "realTradingDefault7":
        start = null;
        end = endOfDay(serverTime);
        break;

      // ---------------- Custom ----------------
      case "realTradingDefault8":
        return;

      default:
        return;
    }

    setStartDate(start);
    setEndDate(end);
    if (onChange) onChange(start, end);
  };

  // ---------------- HANDLE DATE CLICK ----------------
  const handleDateClick = (timestamp) => {
    const oneDay = 24 * 60 * 60 * 1000;

    // Helpers
    const startOfDay = (ts) => ts - (ts % oneDay); // 00:00:00
    const endOfDay = (ts) => startOfDay(ts) + oneDay - 1000; // 23:59:59

    if (!startDate || (startDate && endDate)) {
      // First click → set start
      const start = startOfDay(timestamp);
      setStartDate(start);
      setEndDate(null);
      if (onChange) onChange(start, null);
    } else if (timestamp >= startDate) {
      // Second click → set end
      const end = endOfDay(timestamp);
      setEndDate(end);
      if (onChange) onChange(startDate, end);
    } else {
      // Clicked before current start → reset start
      const start = startOfDay(timestamp);
      setStartDate(start);
      setEndDate(null);
      if (onChange) onChange(start, null);
    }
  };

  const handlePrev = () => setStartMonthOffset((prev) => prev - 1);
  const handleNext = () => setStartMonthOffset((prev) => prev + 1);

  const formatDate = (timestamp) => {
    const date = new Date(timestamp * 1000); // Convert to milliseconds
    return date
      .toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false, // Use 24-hour format
      })
      .replace(",", ""); // Remove the comma
  };
  const parseTimestamp = (timestamp) => {
    const msPerDay = 24 * 60 * 60 * 1000;
    const daysSinceEpoch = Math.floor(timestamp / msPerDay);

    let year = 1970;
    let dayCount = daysSinceEpoch;

    while (true) {
      const daysInYear =
        year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0) ? 366 : 365;
      if (dayCount < daysInYear) break;
      dayCount -= daysInYear;
      year++;
    }

    const monthDays = [
      31,
      year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0) ? 29 : 28,
      31,
      30,
      31,
      30,
      31,
      31,
      30,
      31,
      30,
      31,
    ];

    let month = 0;
    while (dayCount >= monthDays[month]) {
      dayCount -= monthDays[month];
      month++;
    }

    const day = dayCount + 1;
    const weekday = (4 + daysSinceEpoch) % 7; // 1970-01-01 was Thursday (4)

    return { year, month, day, weekday };
  };

  // Return timestamp (in ms) for year, month, day
  const getTimestamp = (year, month, day) => {
    const msPerDay = 24 * 60 * 60 * 1000;
    let days = 0;
    for (let y = 1970; y < year; y++) {
      days += y % 4 === 0 && (y % 100 !== 0 || y % 400 === 0) ? 366 : 365;
    }

    const monthDays = [
      31,
      year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0) ? 29 : 28,
      31,
      30,
      31,
      30,
      31,
      31,
      30,
      31,
      30,
      31,
    ];
    for (let m = 0; m < month; m++) days += monthDays[m];
    days += day - 1;
    return days * msPerDay;
  };

  // Get days in the month
  const getMonthDays = (year, month) => {
    const monthDays = [
      31,
      year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0) ? 29 : 28,
      31,
      30,
      31,
      30,
      31,
      31,
      30,
      31,
      30,
      31,
    ];
    const totalDays = monthDays[month];

    const firstDayTs = getTimestamp(year, month, 1);
    const { weekday: firstWeekday } = parseTimestamp(firstDayTs);
    const days = [];

    // Adjust to Monday-based calendar (Mon=0, Sun=6)
    const offset = (firstWeekday + 6) % 7;
    for (let i = 0; i < offset; i++) days.push(null);

    for (let d = 1; d <= totalDays; d++) {
      days.push({ year, month, day: d });
    }
    return days;
  };

  // Month name list
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Main render
  const isRangeDate = (year, month, day) => {
    if (!startDate || !endDate) return false;

    const ts = getTimestamp(year, month, day);

    const s = startDate;
    const e =endDate;

    return ts > s && ts < e;
  };
  const isHoverRange = (year, month, day) => {
  if (!startDate) return false;
  if (endDate) return false; 
  if (!hoverDate) return false;
  const ts = getTimestamp(year, month, day);
  const start = startDate;
  const hoverTS = hoverDate;
  if (hoverTS > start) {
    return ts > start && ts < hoverTS;
  }
  return false;
};
  const renderCalendar = (year, month) => {
    const days = getMonthDays(year, month);
    const monthName = monthNames[month];
    return (
      <div key={`${year}-${month}`} className="calendar-month">
        <div className="calendar-header">
          {monthName} {year}
        </div>
        <div className="calendar-grid">
          {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => (
            <div key={d} className="calendar-day-label">
              {d}
            </div>
          ))}
          {days.map((date, idx) => {
            if (!date)
              return <div key={idx} className="calendar-cell empty"></div>;

            const ts = getTimestamp(date.year, date.month, date.day);
            const startTs = startDate ? Number(startDate) : null;
            const endTs = endDate ? Number(endDate) : null;
            const oneDay = 24 * 60 * 60 * 1000;
            const normalize = (t) => Math.floor(t / oneDay) * oneDay;

            const isSelected =
              (startTs && normalize(ts) === normalize(startTs)) ||
              (endTs && normalize(ts) === normalize(endTs));
            return (
              <div
                key={idx}
                className={`calendar-cell ${
                  isSelected
                    ? "selected"
                    : isRangeDate(date.year, date.month, date.day)
                    ? "daterange"
                    :  isHoverRange(date.year, date.month, date.day)
                    ? "daterange"
                    : ""
                }`}
                onClick={() => handleDateClick && handleDateClick(ts)}
                  onMouseEnter={() => setHoverDate(ts)}
                onMouseLeave={() => setHoverDate(null)}
              >
                {date.day}
              </div>
            );
          })}
        </div>
      </div>
    );
  };
const getAdjustedYearMonth = (year, month) => {
  let y = year;
  let m = month;

  while (m < 0) {
    m += 12;
    y -= 1;
  }

  while (m > 11) {
    m -= 12;
    y += 1;
  }

  return { year: y, month: m };
};

const getVisibleMonths = () => {
  if (!serverTime) return [];

  const d = parseTimestamp(serverTime);

  const currentMonth = d.month - 1;

  const first = getAdjustedYearMonth(
    d.year,
    currentMonth + startMonthOffset - 1
  );

  const second = getAdjustedYearMonth(
    d.year,
    currentMonth + startMonthOffset
  );

  return [first, second];
};
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // ---------------- RENDER ----------------
  return (
    <div
      className="dropdown realTrading_dt mw-100 tradeSummary01"
      ref={dropdownRef}
    >
      <button
        className="btn dropdown-toggle"
        type="button"
        aria-expanded={isDropdownOpen}
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
      >
        <span>
          <i className="fa-solid fa-calendar-days"></i>{" "}
          {selectedOption === "realTradingDefault7"
            ? "All time"
            : startDate && endDate
            ? `${formatDisplayDate(startDate)} - ${formatDisplayDate(endDate)}`
            : ""}
        </span>
        <i className="fa-solid fa-angle-down ms-2"></i>
      </button>

      <div
        className={`${isDropdownOpen ? "dropdown-menu show" : "dropdown-menu"}`}
      >
        <ul>
          {[
            { id: "realTradingDefault1", label: "Today" },
            { id: "realTradingDefault2", label: "Yesterday" },
            { id: "realTradingDefault3", label: "Last 7 days" },
            { id: "realTradingDefault4", label: "Last 30 days" },
            { id: "realTradingDefault5", label: "Current month" },
            { id: "realTradingDefault6", label: "Previous month" },
            { id: "realTradingDefault7", label: "All time" },
          ].map((item) => (
            <li key={item.id}>
              <div
                className={`form-check ${
                  selectedOption === item.id ? "active" : ""
                }`}
              >
                <input
                  className="form-check-input"
                  type="radio"
                  name="flexRadioDefault"
                  id={item.id}
                  checked={selectedOption === item.id}
                  onChange={() => handleRadioChange(item.id)}
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                />
                <label className="form-check-label" htmlFor={item.id}>
                  {item.label}
                </label>
              </div>
            </li>
          ))}
          <li>
            <div className={`form-check`}>
              <input
                className="form-check-input"
                type="radio"
                name="flexRadioDefault"
                id="realTradingDefault8"
                checked={selectedOption === "realTradingDefault8"}
                onChange={() => handleRadioChange("realTradingDefault8")}
              />
              <label className="form-check-label" htmlFor="realTradingDefault8">
                Custom range
              </label>
            </div>
          </li>
        </ul>

        {selectedOption === "realTradingDefault8" && (
          <div className="range-right">
            <div className="date-inputs">
              <input
                type="text"
                readOnly
                value={startDate ? formatDisplayDate(startDate) : ""}
              />
              <input
                type="text"
                readOnly
                value={endDate ? formatDisplayDate(endDate) : ""}
              />
            </div>
            <div className="calendar-bg0">
              <div className="calendar-wrapper">
                <button onClick={handlePrev}>
                  <i className="fa-solid fa-chevron-left"></i>
                </button>
                <button onClick={handleNext}>
                  <i className="fa-solid fa-chevron-right"></i>
                </button>
              </div>
              {getVisibleMonths().map(({ year, month }) =>
                renderCalendar(year, month)
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServerDatePicker;
